import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Spinner } from "../../../components";
import { useAdminUsageTimeseries } from "../../../api/hooks/admin";
import {
  USAGE_CATEGORIES,
  USAGE_CATEGORY_LABELS,
  UsageCategory,
  UsageGranularity,
} from "../../../api/admin/types";
import {
  adjustWorkflowTotal,
  isoFromDateInput,
  MetricsMode,
  visibleCategories,
} from "../shared/constants";
import { formatInt } from "../shared/formatters";
import TrendChart from "../AgencyTotals/TrendChart";

const MAX_BARS = 12;

const _zeroTotals = (): Record<UsageCategory, number> =>
  USAGE_CATEGORIES.reduce(
    (acc, c) => ({ ...acc, [c]: 0 }),
    {} as Record<UsageCategory, number>
  );

interface RoleTotalsProps {
  mode: MetricsMode;
  granularity: UsageGranularity;
  startA: string;
  endA: string;
  startB: string;
  endB: string;
  includeTestAccounts: boolean;
  showEpic: boolean;
  onFetchingChange?: (fetching: boolean) => void;
}

interface RoleRow {
  id: string;
  label: string;
  userCount: number;
  totals: Record<UsageCategory, number>;
  total: number;
}

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: 20,
  width: "100%",
  maxWidth: 1200,
  boxSizing: "border-box",
};

const RoleTotals: React.FC<RoleTotalsProps> = ({
  mode,
  granularity,
  startA,
  endA,
  startB,
  endB,
  includeTestAccounts,
  showEpic,
  onFetchingChange,
}) => {
  const [drillId, setDrillId] = useState<string | null>(null);

  // Compare mode is anchored on period B (the "now") elsewhere on the page;
  // this tab is single-window, so it follows the same anchor.
  const start = mode === "compare" ? startB : startA;
  const end = mode === "compare" ? endB : endA;

  const tsParams =
    start && end
      ? {
          startDate: isoFromDateInput(start),
          endDate: isoFromDateInput(end),
          granularity,
          groupBy: "role" as const,
          includeTestAccounts,
        }
      : null;
  const tsQuery = useAdminUsageTimeseries(tsParams);

  useEffect(() => {
    onFetchingChange?.(tsQuery.isFetching);
  }, [tsQuery.isFetching, onFetchingChange]);

  const rows: RoleRow[] = useMemo(() => {
    const groups = tsQuery.data?.groups ?? [];
    const byGroup = tsQuery.data?.by_group ?? {};
    return groups.map((g) => {
      const buckets = byGroup[g.id] || [];
      const totals = _zeroTotals();
      let total = 0;
      for (const b of buckets) {
        for (const c of USAGE_CATEGORIES) {
          totals[c] += b.totals[c] ?? 0;
        }
        total += b.total;
      }
      return {
        id: g.id,
        label: g.label,
        userCount: g.user_count ?? 0,
        totals,
        total: adjustWorkflowTotal(total, totals, showEpic),
      };
    });
  }, [tsQuery.data, showEpic]);

  const barData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.userCount - a.userCount)
        .slice(0, MAX_BARS)
        .map((r) => ({ label: r.label, users: r.userCount })),
    [rows]
  );

  const tableRows = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total),
    [rows]
  );

  const cats = visibleCategories(showEpic);
  const drillRow = drillId ? rows.find((r) => r.id === drillId) ?? null : null;
  const drillBuckets = drillId ? tsQuery.data?.by_group?.[drillId] : undefined;

  if (tsQuery.isLoading || !tsQuery.data) {
    return (
      <div style={{ padding: 40 }}>
        <Spinner />
      </div>
    );
  }

  const rangeLabel = `${tsQuery.data.start_date.slice(0, 10)} → ${tsQuery.data.end_date.slice(0, 10)}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%" }}>
      <div style={cardStyle}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>Users by role</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          All seats, grouped by the role on the user profile.
          {rows.length > MAX_BARS ? ` Top ${MAX_BARS} of ${rows.length} shown - see the table below for the rest.` : ""}
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={barData} margin={{ top: 8, right: 16, bottom: 60, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              angle={-35}
              textAnchor="end"
              tick={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => [formatInt(v), "Users"]} />
            <Bar dataKey="users" fill="#3949ab" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>Feature usage by role</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          {rangeLabel}. Click a row for its usage trend.
        </div>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ padding: "8px 6px", textAlign: "left", fontSize: 12, color: "#6b7280", fontWeight: 700, borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>
                Role
              </th>
              <th style={{ padding: "8px 6px", textAlign: "right", fontSize: 12, color: "#6b7280", fontWeight: 700, borderBottom: "1px solid #e5e7eb" }}>
                Users
              </th>
              {cats.map((c) => {
                const isCompanion = c === "policies";
                return (
                  <th
                    key={c}
                    style={{
                      padding: "8px 6px",
                      textAlign: "right",
                      fontSize: 12,
                      color: isCompanion ? "#9ca3af" : "#6b7280",
                      fontStyle: isCompanion ? "italic" : "normal",
                      fontWeight: 700,
                      borderBottom: "1px solid #e5e7eb",
                    }}
                    title={isCompanion ? "Uploads only - not counted in Total" : undefined}
                  >
                    {USAGE_CATEGORY_LABELS[c]}
                  </th>
                );
              })}
              <th style={{ padding: "8px 6px", textAlign: "right", fontSize: 12, color: "#6b7280", fontWeight: 700, borderBottom: "1px solid #e5e7eb" }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((r) => (
              <tr
                key={r.id}
                onClick={() => setDrillId(r.id)}
                style={{ cursor: "pointer", opacity: r.total === 0 ? 0.55 : 1 }}
              >
                <td style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", fontWeight: 600 }}>
                  {r.label}
                </td>
                <td style={{ padding: "8px 6px", borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>
                  {formatInt(r.userCount)}
                </td>
                {cats.map((c) => {
                  const isCompanion = c === "policies";
                  return (
                    <td
                      key={c}
                      style={{
                        padding: "8px 6px",
                        borderBottom: "1px solid #f3f4f6",
                        textAlign: "right",
                        color: isCompanion ? "#9ca3af" : undefined,
                        fontStyle: isCompanion ? "italic" : "normal",
                        background: isCompanion ? "#fafafa" : undefined,
                      }}
                    >
                      {formatInt(r.totals[c])}
                    </td>
                  );
                })}
                <td style={{ padding: "8px 6px", borderBottom: "1px solid #f3f4f6", textAlign: "right", fontWeight: 700 }}>
                  {formatInt(r.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drillRow && drillBuckets ? (
        <TrendChart
          buckets={drillBuckets}
          title={`${drillRow.label} — usage trend`}
          subtitle={`${rangeLabel}, by ${granularity}`}
          showEpic={showEpic}
        />
      ) : null}
    </div>
  );
};

export default RoleTotals;
